/**
 * Registro único de navegación: estructura, permisos, atajos y qué direcciones
 * pertenecen a cada entrada.
 *
 * Es un árbol de tres niveles como máximo: sección, espacio y entrada. El `id`
 * es la identidad de cada entrada y de cada espacio (textos en `@/copy/nav`,
 * estado de espacios abiertos, pruebas): la dirección no alcanza porque las
 * entradas de IA comparten `/ai-hub` y se distinguen por la pestaña.
 *
 * Nombres y descripciones NO viven acá: están en `NAV_META` (`src/copy/nav.ts`),
 * indexados por id. Las funciones puras que lo consumen están al lado:
 * `visibilidad.ts`, `entrada-activa.ts` y `migas.ts`.
 *
 * Una pantalla nueva se ubica con el protocolo de `.claude/rules/navegacion.md`.
 */
import {
  Building2,
  FolderKanban,
  Globe,
  HandCoins,
  HelpCircle,
  Inbox,
  LayoutDashboard,
  ListChecks,
  ListTodo,
  Mail,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Server,
  Settings,
  Sparkles,
  Target,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { PERMISO_PRIVACIDAD } from '@/features/privacidad/permiso';
import type { AppPermission } from '@/types/app-permissions';

/**
 * Una dirección que una entrada cubre además de la suya. `params` restringe por
 * parámetros de la dirección (`?tab=`): cada clave admite una lista de valores
 * y `''` significa «el parámetro no está».
 */
export interface Patron {
  /** Ruta con segmentos fijos y `:parametros` (`/ai-hub/agents/:agentKey`). */
  path: string;
  params?: Record<string, string[]>;
}

export type Acceso = { permiso: AppPermission } | 'todos' | 'superadmin';

export interface Entrada {
  tipo: 'entrada';
  id: string;
  /** Dirección que abre la entrada; puede llevar `?tab=` / `&sub=`. */
  to: string;
  icon?: LucideIcon;
  acceso: Acceso;
  /** Atajo numérico global (0-9). */
  kbd?: string;
  cubre?: Patron[];
  contador?: 'leads-sin-leer';
}

export interface Espacio {
  tipo: 'espacio';
  id: string;
  icon: LucideIcon;
  entradas: Entrada[];
}

export type Elemento = Entrada | Espacio;

export interface Seccion {
  id: string;
  /** `null`: las entradas de arriba de todo, sin título. */
  titulo: string | null;
  elementos: Elemento[];
}

const entrada = (e: Omit<Entrada, 'tipo'>): Entrada => ({ tipo: 'entrada', ...e });
const espacio = (e: Omit<Espacio, 'tipo'>): Espacio => ({ tipo: 'espacio', ...e });
const con = (permiso: AppPermission): Acceso => ({ permiso });

export const REGISTRO: Seccion[] = [
  {
    id: 'arriba',
    titulo: null,
    elementos: [
      entrada({ id: 'inicio', to: '/dashboard', icon: LayoutDashboard, acceso: 'todos', kbd: '1' }),
      entrada({ id: 'sol', to: '/sol', icon: MessageCircle, acceso: 'todos', kbd: '8' }),
      entrada({ id: 'chat', to: '/chat', icon: MessageSquare, acceso: con('messages.send'), kbd: '7' }),
    ],
  },
  {
    id: 'comercial',
    titulo: 'Comercial',
    elementos: [
      espacio({
        id: 'ventas',
        icon: Target,
        entradas: [
          entrada({ id: 'prospectos', to: '/prospects', icon: Users, acceso: con('prospects.read.own'), kbd: '2' }),
          entrada({
            id: 'leads',
            to: '/leads',
            icon: Inbox,
            acceso: con('leads.read.own'),
            kbd: '3',
            contador: 'leads-sin-leer',
          }),
          entrada({ id: 'casos', to: '/casos-de-exito', acceso: con('case_studies.view') }),
        ],
      }),
      espacio({
        id: 'marketing',
        icon: Megaphone,
        entradas: [
          entrada({ id: 'campanas', to: '/campaigns', icon: Mail, acceso: con('campaigns.read.own'), kbd: '4' }),
          entrada({
            id: 'newsletter',
            to: '/campaigns/newsletters',
            acceso: con('newsletters.manage'),
          }),
          entrada({ id: 'reglas-por-red', to: '/admin/reglas-por-red', acceso: con('campaigns.read.own') }),
        ],
      }),
    ],
  },
  {
    id: 'entrega',
    titulo: 'Entrega',
    elementos: [
      entrada({ id: 'proyectos', to: '/proyectos', icon: FolderKanban, acceso: con('projects.view'), kbd: '6' }),
      entrada({ id: 'tareas', to: '/tareas', icon: ListChecks, acceso: con('tasks.view'), kbd: '5' }),
      entrada({ id: 'backlogs', to: '/backlogs', icon: ListTodo, acceso: con('backlogs.view') }),
    ],
  },
  {
    id: 'negocio',
    titulo: 'Negocio',
    elementos: [
      espacio({
        id: 'clientes',
        icon: Globe,
        entradas: [
          entrada({ id: 'partners', to: '/partners', acceso: con('partners.view') }),
          entrada({ id: 'entornos', to: '/admin/clientes', acceso: con('clients.view') }),
          entrada({ id: 'metricas-soporte', to: '/soporte/metricas', acceso: con('support.metrics.view') }),
        ],
      }),
      espacio({
        id: 'finanzas',
        icon: HandCoins,
        entradas: [
          entrada({ id: 'cobranzas', to: '/cobranzas', acceso: con('collections.view.own'), kbd: '9' }),
          entrada({ id: 'contratos', to: '/contratos', acceso: con('collections.view.own') }),
          entrada({ id: 'facturacion', to: '/facturacion', acceso: con('invoices.view') }),
          entrada({ id: 'gastos', to: '/gastos', acceso: con('expenses.manage') }),
          entrada({ id: 'arca', to: '/admin/facturacion', acceso: con('invoices.manage') }),
          entrada({
            id: 'cuentas-para-cobrar',
            to: '/cuentas-para-cobrar',
            acceso: con('collections.manage'),
          }),
        ],
      }),
    ],
  },
  {
    id: 'configuracion',
    titulo: 'Configuración',
    elementos: [
      espacio({
        id: 'empresa',
        icon: Building2,
        entradas: [
          entrada({ id: 'datos-empresa', to: '/organization', acceso: con('organization.read.all') }),
          entrada({ id: 'catalogo', to: '/catalog', acceso: con('catalog.read.all') }),
        ],
      }),
      espacio({
        id: 'ia',
        icon: Sparkles,
        entradas: [
          entrada({
            id: 'ia-panel',
            to: '/ai-hub',
            acceso: con('ai_rules.read.all'),
            cubre: [{ path: '/ai-hub', params: { tab: ['', 'dashboard'] } }],
          }),
          entrada({
            id: 'ia-agentes',
            to: '/ai-hub?tab=agents',
            acceso: con('ai_rules.read.all'),
            cubre: [{ path: '/ai-hub/agents/:agentKey' }],
          }),
          // Modelos reúne asignaciones, catálogo y rotación (`?sub=assignments`
          // es la subpestaña vieja y abre la misma vista). `?sub=controls` ya no
          // existe: la pantalla lo lleva a Contexto.
          entrada({
            id: 'ia-modelos',
            to: '/ai-hub?tab=config&sub=models',
            acceso: con('ai_rules.read.all'),
            cubre: [{ path: '/ai-hub', params: { tab: ['config'] } }],
          }),
          entrada({
            id: 'ia-reglas',
            to: '/ai-hub?tab=rules',
            acceso: con('ai_rules.read.all'),
            cubre: [{ path: '/ai-hub', params: { tab: ['rules', 'denylist'] } }],
          }),
          entrada({
            id: 'ia-contexto',
            to: '/ai-hub?tab=config&sub=context',
            acceso: con('ai_rules.read.all'),
          }),
          entrada({ id: 'ia-telegram', to: '/admin/ai-config', acceso: con('telegram.users.manage') }),
          entrada({ id: 'ia-actividad', to: '/admin/sol', acceso: con('agent_logs.read.all') }),
        ],
      }),
      espacio({
        id: 'personas',
        icon: Users,
        entradas: [
          // Equipo cubre /equipo con cualquier pestaña de equipo (y el perfil de
          // una persona); las de acceso las gana Cuentas y roles por ser más específica.
          entrada({ id: 'equipo', to: '/equipo', acceso: con('employees.view') }),
          entrada({
            id: 'cuentas-y-roles',
            to: '/equipo?tab=cuentas',
            acceso: con('users.manage'),
            cubre: [{ path: '/equipo', params: { tab: ['cuentas', 'roles'] } }],
          }),
        ],
      }),
      espacio({
        id: 'sistema',
        icon: Server,
        entradas: [
          entrada({ id: 'infraestructura', to: '/admin/infraestructura', acceso: con('infra.view') }),
          entrada({ id: 'auditoria', to: '/admin/audit', acceso: con('audit.read') }),
          entrada({ id: 'copias', to: '/admin/backups', acceso: 'superadmin' }),
          entrada({ id: 'privacidad', to: '/privacidad', acceso: con(PERMISO_PRIVACIDAD) }),
        ],
      }),
    ],
  },
  {
    id: 'cuenta',
    titulo: 'Cuenta',
    elementos: [
      entrada({
        id: 'ajustes',
        to: '/settings',
        icon: Settings,
        acceso: 'todos',
        kbd: '0',
        // Conexiones pasa a Ajustes como «Tu correo y calendario».
        cubre: [{ path: '/connections' }],
      }),
      entrada({ id: 'ayuda', to: '/ayuda', icon: HelpCircle, acceso: 'todos' }),
    ],
  },
];

/** Todas las entradas del árbol, en orden, con su espacio y su sección. */
export interface EntradaUbicada {
  entrada: Entrada;
  espacio: Espacio | null;
  seccion: Seccion;
}

export function entradasDe(registro: Seccion[] = REGISTRO): EntradaUbicada[] {
  const salida: EntradaUbicada[] = [];
  for (const seccion of registro) {
    for (const el of seccion.elementos) {
      if (el.tipo === 'entrada') salida.push({ entrada: el, espacio: null, seccion });
      else for (const e of el.entradas) salida.push({ entrada: e, espacio: el, seccion });
    }
  }
  return salida;
}

/** Ids de entradas y de espacios (las secciones no tienen texto propio). */
export function idsDe(registro: Seccion[] = REGISTRO): string[] {
  return registro.flatMap((s) =>
    s.elementos.flatMap((el) => (el.tipo === 'entrada' ? [el.id] : [el.id, ...el.entradas.map((e) => e.id)])),
  );
}
